import * as React from 'react';
import { AppUser } from 'Src/views/App/App';
import { SchoolFileCategory } from 'Src/models/schoolFile';
import {
  createSchoolFileCategory,
  deleteSchoolFileCategory,
  updateSchoolFileCategory
} from 'Src/helpers/service/admin/documents';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { Switch } from 'Src/components/Switch/Switch';
import { ListEmptyState } from 'Src/components/ListEmptyState/ListEmptyState';
import { faFolderOpen } from '@fortawesome/free-solid-svg-icons';

interface Props {
  user: AppUser;
  categories: SchoolFileCategory[];
  onReload: () => Promise<any>;
}

export const FileCategoriesManager = ({ user, categories, onReload }: Props) => {
  const [editing, setEditing] = React.useState<SchoolFileCategory | undefined>();
  const [isCreating, setIsCreating] = React.useState(false);
  const [name, setName] = React.useState('');
  const [isPublished, setIsPublished] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [categoryToDelete, setCategoryToDelete] = React.useState<SchoolFileCategory | undefined>();
  const [deleting, setDeleting] = React.useState(false);

  const openCreate = () => {
    setEditing(undefined);
    setName('');
    setIsPublished(true);
    setIsCreating(true);
  };
  const openEdit = (category: SchoolFileCategory) => {
    setEditing(category);
    setName(category.name);
    setIsPublished(category.isPublished);
    setIsCreating(false);
  };
  const close = () => {
    if (saving) return;
    setEditing(undefined);
    setIsCreating(false);
  };
  const save = () => {
    const cleanName = name.trim().replace(/\s+/g, ' ');
    if (!cleanName || saving) return;
    setSaving(true);
    const request = editing
      ? updateSchoolFileCategory(user, editing.id, { name: cleanName, isPublished })
      : createSchoolFileCategory(user, { name: cleanName, isPublished, sortOrder: categories.length });
    request.then(() => onReload()).then(() => {
      setSaving(false);
      setEditing(undefined);
      setIsCreating(false);
    }, () => setSaving(false));
  };
  const remove = () => {
    if (!categoryToDelete || deleting) return;
    setDeleting(true);
    deleteSchoolFileCategory(user, categoryToDelete.id)
      .then(() => onReload())
      .then(() => {
        setDeleting(false);
        setCategoryToDelete(undefined);
      }, () => setDeleting(false));
  };

  return (
    <div className="schoolFileCategories">
      <div className="schoolFileCategories__toolbar">
        <div><strong>Document categories</strong><span>Only published categories appear under Resources on the public website.</span></div>
        <button type="button" onClick={openCreate}>+ Create category</button>
      </div>
      <div className="schoolFileCategories__tableWrap">
        <table>
          <thead><tr><th>Category</th><th>Public website</th><th /></tr></thead>
          <tbody>
            {categories.map(category => (
              <tr key={category.id || category.key} onClick={() => openEdit(category)}>
                <td><strong>{category.name}</strong><small>{category.key}</small></td>
                <td><span className={category.isPublished ? 'isPublished' : ''}>{category.isPublished ? 'Published' : 'Hidden'}</span></td>
                <td onClick={event => event.stopPropagation()}>
                  <button type="button" onClick={() => openEdit(category)}>Edit</button>
                  <button type="button" className="isDanger" onClick={() => setCategoryToDelete(category)}>Delete</button>
                </td>
              </tr>
            ))}
            {categories.length === 0 ? (
              <tr className="schoolFileCategories__emptyRow">
                <td colSpan={3}>
                  <ListEmptyState
                    icon={faFolderOpen}
                    title="No document categories yet"
                    description="Create a category to organise downloadable documents on the public website."
                  />
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      <SimpleModal
        isOpen={isCreating || !!editing}
        title={editing ? 'Edit category' : 'Create category'}
        onCloseClick={close}
        onBackdropClick={close}
        buttonCancelText="Cancel"
        buttonText={saving ? 'Saving...' : editing ? 'Save changes' : 'Create category'}
        buttonDisabled={saving || !name.trim()}
        onButtonClick={save}
        isBodyBold={false}
        customClass="schoolFileCategoryModal"
        dialogStyle={{ maxWidth: 500 }}
        body={
          <div className="schoolFileCategoryModal__body">
            <label><span>Category name</span><input type="text" autoFocus value={name} maxLength={80} onChange={event => setName(event.target.value)} /></label>
            <div className="schoolFileCategoryModal__visibility">
              <Switch
                name="isCategoryPublished"
                value={isPublished}
                customClass=""
                onChange={event => setIsPublished(event.target.checked)}
              />
              <span><strong>Publish on public website</strong><small>Show this category and its documents under Resources.</small></span>
            </div>
          </div>
        }
      />
      <SimpleModal
        isOpen={!!categoryToDelete}
        title="Delete category?"
        onCloseClick={() => !deleting && setCategoryToDelete(undefined)}
        onBackdropClick={() => !deleting && setCategoryToDelete(undefined)}
        buttonCancelText="Cancel"
        buttonText={deleting ? 'Deleting...' : 'Delete category'}
        buttonDisabled={deleting}
        onButtonClick={remove}
        isBodyBold={false}
        customClass="schoolFileCategoryDeleteModal"
        dialogStyle={{ maxWidth: 500 }}
        body={
          <div>
            Category <strong>{categoryToDelete ? `“${categoryToDelete.name}”` : ''}</strong> will be deleted.
            Documents assigned to it will remain in the admin console, but will no longer appear on the public website.
          </div>
        }
      />
    </div>
  );
};
